import type { Components } from '@/api/generated/api';
import { PerformanceAssessmentDocumentationMode, ObservationTimelineEntryType } from '@/api/generated/api.enum';
import { assertArrayNotEmpty } from '@/utils/assertion.utils';
import {
  getPerformanceAssessmentsForSchoolSubject,
  normalizePerformanceAssessmentOrder,
} from '@/utils/performance.utils';
import { getOrderedSchoolSubjects } from 'common/utils/school.subject.utils';
import { isStudentEligibleForSubject } from '@/utils/students.utils';
import { areArraysEqualUnordered, toRecord, uniquePrimitives } from 'common/utils/array.utils';
import { EQuickEntryCommandTrigger, EQuickEntryEscapeAction, EQuickEntryKind } from './quickEntry.enums';

// Future entry kinds extend this union without coupling timeline-only handlers to them.
export interface IQuickEntryDraft {
  kind: EQuickEntryKind;
  text: string;
  date: string; // ISO date-time
  schoolSubjectId: number | null;
  performanceAssessmentId: number | null;
  /** Selects every student eligible for the active subject; this is not necessarily the whole class. */
  allEligibleStudentsSelected: boolean;
  studentIds: number[];
  competenceIds: number[];
  overallPerformanceLevelV2Id: number | null;
  gradeValue: number | null;
  competenceLevelById: Record<number, number | null>;
  gradeCalculationTagId: number | null;
}

export interface IQuickEntryFixedContext {
  studentIds?: readonly number[];
  schoolSubjectId?: number;
  performanceAssessmentId?: number;
  competenceIds?: readonly number[];
  /** Limits competence selection to one domain without fixing the individual competence links. */
  competenceDomainId?: number;
}

export interface IQuickEntryVisibilityHint {
  message: string;
  requireAtLeastOneCompetence?: boolean;
  requiredStudentId?: number;
}

export type TQuickEntryCreatedPayload =
  | {
      type: typeof ObservationTimelineEntryType.performanceAssessmentEntry;
      performanceAssessmentEntry: Components.Schemas.PerformanceAssessmentEntryDto;
    }
  | { type: typeof ObservationTimelineEntryType.note; note: Components.Schemas.NoteDto }
  | {
      type: typeof ObservationTimelineEntryType.supportMeasureEntry;
      supportMeasureEntry: Components.Schemas.SupportMeasureEntryDto;
    };

export type TQuickEntryPostCreateAction = 'open-details';

export interface IQuickEntryPreselection {
  kind?: EQuickEntryKind;
  schoolSubjectId?: number | null;
  performanceAssessmentId?: number | null;
  studentIds?: readonly number[];
  competenceIds?: readonly number[];
}

export interface IQuickEntryGrowthFlowParams {
  schoolClassId: number;
  allowTrialUpsellAfterEntry?: boolean;
}

export interface IQuickEntryDialogShowParams extends IQuickEntryPreselection {
  /** Matches the legacy create dialog: first ordered subject, then the last-viewed or first eligible assessment. */
  preselectDefaultPerformanceAssessment?: boolean;
  /** Demo-class first entries show off a rating visual: the first eligible assessment with one wins over the default. */
  preferVisualPerformanceAssessment?: boolean;
  /** Preserves the first-entry success/detail lifecycle when QuickEntry is launched by a growth flow. */
  growthFlow?: IQuickEntryGrowthFlowParams;
}

export interface ICommandToken {
  trigger: EQuickEntryCommandTrigger;
  query: string;
  startIndex: number;
}

export interface ISubjectChangeImpact {
  droppedCompetenceIds: number[];
  droppedStudentIds: number[];
  droppedPerformanceAssessment: boolean;
}

type TSubjectScope = Pick<Components.Schemas.SchoolSubjectDto, 'id' | 'useSubjectSpecificAssessments'>;
type TOrderedSubjectScope = TSubjectScope & Pick<Components.Schemas.SchoolSubjectDto, 'groupId' | 'order'>;
type TSubjectGroupOrderScope = Pick<Components.Schemas.SchoolSubjectGroupDto, 'id' | 'order'>;
type TAssessmentScope = Pick<Components.Schemas.BasePerformanceAssessmentDto, 'id' | 'order' | 'schoolSubjectId'>;
type TDefaultAssessmentScope = TAssessmentScope &
  Pick<Components.Schemas.BasePerformanceAssessmentDto, 'performanceVisualId'>;
type TStudentScope = Pick<Components.Schemas.StudentWithSubjectAccessDto, 'id' | 'schoolClassRelation'>;
type TDomainScope = Pick<Components.Schemas.CompetenceDomainDto, 'schoolSubjectId'> &
  Partial<Pick<Components.Schemas.CompetenceDomainDto, 'id'>> & {
    competences: Array<Pick<Components.Schemas.CompetenceDto, 'id'>>;
  };

export interface IQuickEntryCompetenceRelationMaps {
  domainIdByCompetenceId: ReadonlyMap<number, number>;
  schoolSubjectIdByCompetenceId: ReadonlyMap<number, number | null>;
  schoolSubjectIdByDomainId: ReadonlyMap<number, number | null>;
}

// a trigger only counts at the start of the input or after whitespace, and runs up to the caret
const COMMAND_TOKEN_PATTERN = /(^|\s)([@/])([^\s@/]*)$/;

export const createEmptyQuickEntryDraft = (date: string): IQuickEntryDraft => ({
  kind: EQuickEntryKind.PerformanceAssessment,
  text: '',
  date,
  schoolSubjectId: null,
  performanceAssessmentId: null,
  allEligibleStudentsSelected: true,
  studentIds: [],
  competenceIds: [],
  overallPerformanceLevelV2Id: null,
  gradeValue: null,
  competenceLevelById: {},
  gradeCalculationTagId: null,
});

export const shouldShowQuickEntryCollectiveRating = (params: {
  competenceCount: number;
  hasCompetenceAssessment: boolean;
  hasOverallRating: boolean;
  hasSelectedPerformanceAssessment: boolean;
  kind: EQuickEntryKind;
  studentCount: number;
}): boolean =>
  params.kind === EQuickEntryKind.PerformanceAssessment &&
  params.hasCompetenceAssessment &&
  params.hasSelectedPerformanceAssessment &&
  params.studentCount >= 2 &&
  (params.hasOverallRating || params.competenceCount > 0);

export const serializeQuickEntryDraft = (draft: IQuickEntryDraft): string =>
  JSON.stringify({
    kind: draft.kind,
    text: draft.text,
    date: draft.date,
    schoolSubjectId: draft.schoolSubjectId,
    performanceAssessmentId: draft.performanceAssessmentId,
    allEligibleStudentsSelected: draft.allEligibleStudentsSelected,
    studentIds: [...draft.studentIds].sort((a, b) => a - b),
    competenceIds: [...draft.competenceIds].sort((a, b) => a - b),
    overallPerformanceLevelV2Id: draft.overallPerformanceLevelV2Id,
    gradeValue: draft.gradeValue,
    competenceLevelById: Object.fromEntries(
      Object.entries(draft.competenceLevelById).sort(([a], [b]) => Number(a) - Number(b)),
    ),
    gradeCalculationTagId: draft.gradeCalculationTagId,
  });

export const parseCommandToken = (text: string, caretIndex: number): ICommandToken | null => {
  const match = COMMAND_TOKEN_PATTERN.exec(text.slice(0, caretIndex));
  if (!match) return null;

  const query = match[3];
  return {
    trigger: match[2] as EQuickEntryCommandTrigger,
    query,
    startIndex: caretIndex - query.length - 1,
  };
};

export const consumeCommandToken = (
  text: string,
  caretIndex: number,
  token: ICommandToken,
): { text: string; caretIndex: number } => ({
  text: text.slice(0, token.startIndex) + text.slice(caretIndex),
  caretIndex: token.startIndex,
});

// mirrors the timeline filter: with a subject the create-dialog rule applies
// (subject-specific vs shared), without one the shared assessments are offered
export const getEligiblePerformanceAssessments = <T extends TAssessmentScope>(
  assessments: T[],
  schoolSubject: TSubjectScope | null,
): T[] => {
  if (schoolSubject) return getPerformanceAssessmentsForSchoolSubject(assessments, schoolSubject);
  return normalizePerformanceAssessmentOrder(assessments.filter((assessment) => assessment.schoolSubjectId == null));
};

interface IQuickEntryDefaultPerformanceAssessmentSelectionParams {
  schoolSubjects: TOrderedSubjectScope[];
  schoolSubjectGroups: TSubjectGroupOrderScope[];
  performanceAssessments: TDefaultAssessmentScope[];
  lastViewedPerformanceAssessmentId: number | null;
  preferVisualPerformanceAssessment?: boolean;
  schoolSubjectId?: number | null;
}

export const getQuickEntryDefaultPerformanceAssessmentSelection = (
  params: IQuickEntryDefaultPerformanceAssessmentSelectionParams,
): { schoolSubjectId: number | null; performanceAssessmentId: number | null } => {
  const schoolSubject =
    params.schoolSubjectId === undefined
      ? (getOrderedSchoolSubjects(params.schoolSubjects, params.schoolSubjectGroups).at(0) ?? null)
      : (params.schoolSubjects.find((subject) => subject.id === params.schoolSubjectId) ?? null);
  const eligiblePerformanceAssessments = getEligiblePerformanceAssessments(
    params.performanceAssessments,
    schoolSubject,
  );
  const visualPerformanceAssessment = params.preferVisualPerformanceAssessment
    ? eligiblePerformanceAssessments.find((assessment) => assessment.performanceVisualId != null)
    : undefined;
  const performanceAssessment =
    visualPerformanceAssessment ??
    eligiblePerformanceAssessments.find((assessment) => assessment.id === params.lastViewedPerformanceAssessmentId) ??
    eligiblePerformanceAssessments.at(0) ??
    null;

  return {
    schoolSubjectId: schoolSubject?.id ?? null,
    performanceAssessmentId: performanceAssessment?.id ?? null,
  };
};

export const resolveQuickEntryDialogPreselection = (
  params: IQuickEntryDialogShowParams,
  defaultSelectionContext: Omit<
    IQuickEntryDefaultPerformanceAssessmentSelectionParams,
    'schoolSubjectId' | 'preferVisualPerformanceAssessment'
  >,
): IQuickEntryPreselection => {
  const preselection: IQuickEntryPreselection = {
    kind: params.kind,
    schoolSubjectId: params.schoolSubjectId,
    performanceAssessmentId: params.performanceAssessmentId,
    studentIds: params.studentIds,
    competenceIds: params.competenceIds,
  };
  const canApplyPerformanceAssessmentDefault =
    params.kind == null || params.kind === EQuickEntryKind.PerformanceAssessment;
  if (!params.preselectDefaultPerformanceAssessment || !canApplyPerformanceAssessmentDefault) return preselection;

  const defaultSelection = getQuickEntryDefaultPerformanceAssessmentSelection({
    ...defaultSelectionContext,
    preferVisualPerformanceAssessment: params.preferVisualPerformanceAssessment,
    schoolSubjectId: params.schoolSubjectId,
  });

  return {
    ...preselection,
    schoolSubjectId: params.schoolSubjectId === undefined ? defaultSelection.schoolSubjectId : params.schoolSubjectId,
    performanceAssessmentId:
      params.performanceAssessmentId === undefined
        ? defaultSelection.performanceAssessmentId
        : params.performanceAssessmentId,
  };
};

export interface IQuickEntryLastUsedType {
  type: EQuickEntryKind | null;
  performanceAssessmentId: number | null;
}

/** A fixed context or an explicit host choice wins over the remembered type; eligibility is left to the draft. */
export const applyLastUsedQuickEntryType = (
  preselection: IQuickEntryPreselection | undefined,
  lastUsed: IQuickEntryLastUsedType,
  options: { fixedContext?: IQuickEntryFixedContext; hasSupportMeasures: boolean },
): IQuickEntryPreselection | undefined => {
  if (options.fixedContext?.performanceAssessmentId !== undefined) return preselection;
  if (preselection?.kind !== undefined || preselection?.performanceAssessmentId != null) return preselection;

  switch (lastUsed.type) {
    case EQuickEntryKind.PerformanceAssessment:
      if (lastUsed.performanceAssessmentId == null) return preselection;
      return { ...preselection, performanceAssessmentId: lastUsed.performanceAssessmentId };
    case EQuickEntryKind.Note:
      return { ...preselection, kind: EQuickEntryKind.Note };
    case EQuickEntryKind.SupportMeasure:
      return options.hasSupportMeasures ? { ...preselection, kind: EQuickEntryKind.SupportMeasure } : preselection;
    default:
      return preselection;
  }
};

export const buildQuickEntryCompetenceRelationMaps = (
  domains: readonly TDomainScope[],
): IQuickEntryCompetenceRelationMaps => {
  const domainIdByCompetenceId = new Map<number, number>();
  const schoolSubjectIdByCompetenceId = new Map<number, number | null>();
  const schoolSubjectIdByDomainId = new Map<number, number | null>();

  for (const domain of domains) {
    if (domain.id != null) schoolSubjectIdByDomainId.set(domain.id, domain.schoolSubjectId);
    for (const competence of domain.competences) {
      if (domain.id != null) domainIdByCompetenceId.set(competence.id, domain.id);
      schoolSubjectIdByCompetenceId.set(competence.id, domain.schoolSubjectId);
    }
  }

  return { domainIdByCompetenceId, schoolSubjectIdByCompetenceId, schoolSubjectIdByDomainId };
};

export const isQuickEntryFixedContextValid = (params: {
  draft: Pick<
    IQuickEntryDraft,
    | 'allEligibleStudentsSelected'
    | 'competenceIds'
    | 'kind'
    | 'performanceAssessmentId'
    | 'schoolSubjectId'
    | 'studentIds'
  >;
  eligibleStudentIds: ReadonlySet<number>;
  fixedContext?: IQuickEntryFixedContext;
  relationMaps: IQuickEntryCompetenceRelationMaps;
  selectedPerformanceAssessmentId: number | null;
  selectedSchoolSubjectId: number | null;
}): boolean => {
  const { draft, eligibleStudentIds, fixedContext, relationMaps } = params;
  if (!fixedContext) return true;

  if (
    fixedContext.schoolSubjectId !== undefined &&
    (draft.schoolSubjectId !== fixedContext.schoolSubjectId ||
      params.selectedSchoolSubjectId !== fixedContext.schoolSubjectId)
  ) {
    return false;
  }

  if (
    fixedContext.performanceAssessmentId !== undefined &&
    (draft.kind !== EQuickEntryKind.PerformanceAssessment ||
      draft.performanceAssessmentId !== fixedContext.performanceAssessmentId ||
      params.selectedPerformanceAssessmentId !== fixedContext.performanceAssessmentId)
  ) {
    return false;
  }

  if (fixedContext.studentIds !== undefined) {
    const requestedStudentIds = uniquePrimitives(fixedContext.studentIds);
    if (
      draft.allEligibleStudentsSelected ||
      !areArraysEqualUnordered(draft.studentIds, requestedStudentIds) ||
      requestedStudentIds.some((studentId) => !eligibleStudentIds.has(studentId))
    ) {
      return false;
    }
  }

  if (fixedContext.competenceIds !== undefined) {
    const requestedCompetenceIds = uniquePrimitives(fixedContext.competenceIds);
    if (
      !areArraysEqualUnordered(draft.competenceIds, requestedCompetenceIds) ||
      requestedCompetenceIds.some(
        (competenceId) => relationMaps.schoolSubjectIdByCompetenceId.get(competenceId) !== draft.schoolSubjectId,
      )
    ) {
      return false;
    }
  }

  const fixedDomainId = fixedContext.competenceDomainId;
  if (fixedDomainId === undefined) return true;

  return (
    relationMaps.schoolSubjectIdByDomainId.has(fixedDomainId) &&
    relationMaps.schoolSubjectIdByDomainId.get(fixedDomainId) === draft.schoolSubjectId &&
    draft.competenceIds.every((competenceId) => relationMaps.domainIdByCompetenceId.get(competenceId) === fixedDomainId)
  );
};

export const computeSubjectChangeImpact = (params: {
  draft: Pick<
    IQuickEntryDraft,
    | 'competenceIds'
    | 'studentIds'
    | 'allEligibleStudentsSelected'
    | 'performanceAssessmentId'
    | 'overallPerformanceLevelV2Id'
  >;
  newSchoolSubject: TSubjectScope | null;
  allCompetenceDomains: TDomainScope[];
  allStudents: TStudentScope[];
  allPerformanceAssessments: TAssessmentScope[];
}): ISubjectChangeImpact => {
  const { schoolSubjectIdByCompetenceId } = buildQuickEntryCompetenceRelationMaps(params.allCompetenceDomains);
  const newSubjectId = params.newSchoolSubject?.id ?? null;

  const droppedCompetenceIds = params.draft.competenceIds.filter(
    (competenceId) => newSubjectId == null || schoolSubjectIdByCompetenceId.get(competenceId) !== newSubjectId,
  );

  const studentById: Partial<Record<number, TStudentScope>> = toRecord(
    params.allStudents,
    (student) => student.id,
    (student) => student,
  );
  const droppedStudentIds =
    params.draft.allEligibleStudentsSelected || newSubjectId == null
      ? []
      : params.draft.studentIds.filter((studentId) => {
          const student = studentById[studentId];
          return !student || !isStudentEligibleForSubject(student, newSubjectId);
        });

  const eligibleAssessments = getEligiblePerformanceAssessments(
    params.allPerformanceAssessments,
    params.newSchoolSubject,
  );
  const droppedPerformanceAssessment =
    params.draft.performanceAssessmentId != null &&
    !eligibleAssessments.some((assessment) => assessment.id === params.draft.performanceAssessmentId);

  return {
    droppedCompetenceIds,
    droppedStudentIds,
    droppedPerformanceAssessment,
  };
};

export const nextEscapeAction = (state: {
  suggestionsOpen: boolean;
  popoverOpen: boolean;
  isPristine: boolean;
}): EQuickEntryEscapeAction => {
  if (state.suggestionsOpen) return EQuickEntryEscapeAction.CloseSuggestions;
  if (state.popoverOpen) return EQuickEntryEscapeAction.ClosePopover;
  if (state.isPristine) return EQuickEntryEscapeAction.Collapse;
  return EQuickEntryEscapeAction.ConfirmDiscard;
};

export const getQuickEntryVisibilityHint = (
  hint: IQuickEntryVisibilityHint | undefined,
  draft: Pick<IQuickEntryDraft, 'competenceIds'>,
  resolvedStudentIds: readonly number[],
): string | null => {
  if (!hint) return null;

  const missesCompetence = hint.requireAtLeastOneCompetence === true && draft.competenceIds.length === 0;
  const missesStudent = hint.requiredStudentId !== undefined && !resolvedStudentIds.includes(hint.requiredStudentId);

  return missesCompetence || missesStudent ? hint.message : null;
};

// Keep a newly created entry visible in the current page filters. Entity mutation composables
// synchronize the shared timeline cache, so page-level handlers only manage UI filter state.
export const getCreatedSchoolSubjectId = (payload: TQuickEntryCreatedPayload): number | null => {
  switch (payload.type) {
    case ObservationTimelineEntryType.note:
      return payload.note.schoolSubject?.id ?? null;
    case ObservationTimelineEntryType.performanceAssessmentEntry:
      return payload.performanceAssessmentEntry.schoolSubject.id;
    case ObservationTimelineEntryType.supportMeasureEntry:
      return payload.supportMeasureEntry.schoolSubject.id;
  }
};

export const createQuickEntryCreatedHandler =
  (context: {
    getActiveSchoolSubjectId: () => number | null;
    adoptSchoolSubjectId: (schoolSubjectId: number | null) => void;
    getActiveGrouping?: () => unknown;
    getActiveEntryType?: () =>
      | typeof ObservationTimelineEntryType.note
      | typeof ObservationTimelineEntryType.performanceAssessmentEntry
      | typeof ObservationTimelineEntryType.supportMeasureEntry
      | null;
    clearGrouping?: () => void;
  }) =>
  (payload: TQuickEntryCreatedPayload): void => {
    const activeSchoolSubjectId = context.getActiveSchoolSubjectId();
    const createdSchoolSubjectId = getCreatedSchoolSubjectId(payload);

    if (activeSchoolSubjectId !== null && activeSchoolSubjectId !== createdSchoolSubjectId) {
      context.adoptSchoolSubjectId(createdSchoolSubjectId);
    }

    const activeGrouping = context.getActiveGrouping?.() ?? null;
    const activeEntryType = context.getActiveEntryType?.() ?? null;
    const createdPerformanceAssessmentId =
      payload.type === ObservationTimelineEntryType.performanceAssessmentEntry
        ? (payload.performanceAssessmentEntry.performanceAssessment?.id ?? null)
        : null;
    const entryTypeMismatch = activeEntryType !== null && activeEntryType !== payload.type;
    const assessmentGroupingMismatch =
      activeEntryType === null && activeGrouping !== null && activeGrouping !== createdPerformanceAssessmentId;
    if (entryTypeMismatch || assessmentGroupingMismatch) context.clearGrouping?.();
  };

export const buildQuickEntryNoteCreateDto = (params: {
  draft: IQuickEntryDraft;
  schoolClassId: number;
  /** the materialized recipient snapshot */
  studentIds: number[];
}): Components.Schemas.CreateNoteDto => ({
  schoolClassId: params.schoolClassId,
  text: params.draft.text.trim(),
  date: params.draft.date,
  studentIds: [...params.studentIds],
  competenceIds: [...params.draft.competenceIds],
  ...(params.draft.schoolSubjectId != null ? { schoolSubjectId: params.draft.schoolSubjectId } : {}),
});

export const buildQuickEntrySupportMeasureCreateDto = (params: {
  draft: IQuickEntryDraft;
  schoolClassId: number;
  schoolSubjectId: number;
  studentIds: number[];
}): Components.Schemas.CreateSupportMeasureEntryDto => {
  const studentIds = assertArrayNotEmpty(uniquePrimitives(params.studentIds));
  const description = params.draft.text.trim();

  return {
    schoolClassId: params.schoolClassId,
    schoolSubjectId: params.schoolSubjectId,
    ...(description ? { description } : {}),
    studentIds,
    competenceIds: uniquePrimitives(params.draft.competenceIds),
  };
};

export const buildQuickEntryCreateDto = (params: {
  draft: IQuickEntryDraft;
  schoolClassId: number;
  schoolSubjectId: number;
  performanceAssessmentId: number;
  documentationMode?: Components.Schemas.PerformanceAssessmentDocumentationMode;
  competenceAssessmentId: number;
  label: string;
  studentIds: number[];
}): Components.Schemas.CreatePerformanceAssessmentEntryDto => {
  const description = params.draft.text.trim();
  const competenceIdSet = new Set(params.draft.competenceIds);
  const ratedCompetenceLevels = Object.entries(params.draft.competenceLevelById).filter(
    (entry): entry is [string, number] => entry[1] != null && competenceIdSet.has(Number(entry[0])),
  );

  return {
    schoolClassId: params.schoolClassId,
    schoolSubjectId: params.schoolSubjectId,
    performanceAssessmentId: params.performanceAssessmentId,
    competenceAssessmentId: params.competenceAssessmentId,
    label: params.label,
    date: params.draft.date,
    studentIds: [...params.studentIds],
    competenceIds: [...params.draft.competenceIds],
    ...(params.draft.gradeCalculationTagId != null
      ? { gradeCalculationTagId: params.draft.gradeCalculationTagId }
      : {}),
    ...(description ? { description } : {}),
    ...(params.documentationMode === PerformanceAssessmentDocumentationMode.SCHOOL_GRADE
      ? { gradeValue: params.draft.gradeValue }
      : {}),
    ...(params.documentationMode !== PerformanceAssessmentDocumentationMode.SCHOOL_GRADE &&
    params.draft.overallPerformanceLevelV2Id != null
      ? { performanceLevelV2Id: params.draft.overallPerformanceLevelV2Id }
      : {}),
    ...(ratedCompetenceLevels.length
      ? { competenceIdToPerformanceLevelV2Id: Object.fromEntries(ratedCompetenceLevels) }
      : {}),
  };
};
